import { useMergeRefs } from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import { type FC, type HTMLProps, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Video } from '#components/Video'
import type { GalleryProperties } from './Gallery'

const mediaListClass = css`
  --gallery-gap: 8px;

  flex: 1 1 auto;
  min-width: 0;
  

  scrollbar-width: none;
  overflow-x: auto;
  
  
  display: flex;
  flex-direction: row;
  overflow-y: hidden;
  gap: var(--gallery-gap);
  // align-items: stretch; 
  
  align-items: stretch;

  
  > .entry, > .video {
    &:first-child {
      margin-left: var(--first-pad);
    }
    &:last-child {
      margin-right: var(--last-pad);
    }
    filter: none;
    transition: 0.5s ease filter;

    &.unfocused, > .unfocused {
      filter: brightness(0.3);
    }
  }

  > img, > .video, > .video > .video {
    margin: 0 calc(var(--gallery-gap) / 2);
    width: auto;
    height: 100%;

    max-height: 100%;
    max-width: 100%;
    flex: 0 0 auto;
    object-fit: contain;
    box-sizing: border-box;
  }
`

const scrollIntoViewOptions = { behavior: 'smooth', block: 'center', inline: 'center', container: 'nearest' } as const
const elementIndex = (elements: (HTMLImageElement | HTMLVideoElement | null)[], scrollLeft: number, gap: number, galleryWidth: number) => {
  let offset = 0
  for (let i = 0; i < elements.length; i += 1) {
    const element = elements[i]
    if (element === null) {
      continue
    }

    const box = element.getBoundingClientRect()
    // the gap increments by a full step per item (so full gap width)
    // and then overreaches half to the next item
    let extraOffset = 0
    if (i === 0 || i === elements.length - 1) {
      extraOffset = (galleryWidth - box.width) / 2
    }

    const gapAlign = gap * Math.min(2, i + 1)
    const nextOffset = offset + box.width + gapAlign + extraOffset
    if (nextOffset > scrollLeft) {
      return i
    }
    offset = nextOffset
  }
  return -1
}

export const MediaList: FC<
  {
    items: GalleryProperties['items']
    currentIndex: number
    onItemRef?: (index: number, instance: HTMLImageElement | HTMLVideoElement | null) => void
    onItemSelected?: (index: number, item: GalleryProperties['items'][number]) => void
    onItemFocused?: (index: number, item: GalleryProperties['items'][number]) => void
    gap?: number
  } & HTMLProps<HTMLDivElement>
> = ({
  items,
  currentIndex: externalCurrentIndex,
  onItemRef,
  className,
  ref: externalListRef,
  onPointerDown: externalOnPointerDown,
  onItemSelected,
  onItemFocused,
  gap = 0,
  ...props
}) => {
  const [playState, setPlayState] = useState<'playing' | 'stopped' | 'paused'>('stopped')
  const internalListRef = useRef<HTMLDivElement>(null)
  const listRef = useMergeRefs([externalListRef, internalListRef])
  const internalItemRefs = useRef<(HTMLImageElement | HTMLVideoElement | null)[]>([])
  const internalScrollIndex = useRef(externalCurrentIndex)
  internalItemRefs.current.length = items.length
  const setItemRef = useCallback(
    (i: number, ref: HTMLImageElement | HTMLVideoElement | null) => {
      internalItemRefs.current[i] = ref
      onItemRef?.(i, ref)
    },
    [onItemRef],
  )

  const internalOnPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      externalOnPointerDown?.(e)
      if (!internalListRef.current) {
        return
      }
      const bbox = internalListRef.current.getBoundingClientRect()
      const documentWidth = window.innerWidth
      let index = 0

      const startX = e.screenX
      let deltaPassed = false

      const onPointerMove = (e: PointerEvent) => {
        const delta = Math.abs(e.screenX - startX)
        if (!deltaPassed && delta < 20) {
          return
        }
        deltaPassed = true
        if (internalListRef.current) {
          internalListRef.current.scrollBy({ left: -e.movementX * (documentWidth / (bbox.width + documentWidth * 0.35)), behavior: 'auto' })
          index = elementIndex(
            internalItemRefs.current,
            internalListRef.current.scrollLeft + bbox.width / 2,
            gap,
            internalListRef.current.clientWidth,
          )

          for (let i = 0; i < internalItemRefs.current.length; i += 1) {
            const item = internalItemRefs.current[i]
            item?.classList.add(i === index ? 'focused' : 'unfocused')
            item?.classList.remove(i === index ? 'unfocused' : 'focused')
          }

          onItemFocused?.(index, items[index])
        }
      }
      const onPointerExit = (_e: PointerEvent) => {
        if (internalListRef.current && deltaPassed) {
          const item = internalItemRefs.current.at(index)
          if (item) {
            item.scrollIntoView(scrollIntoViewOptions)
            internalScrollIndex.current = index
          }
          const element = items.at(index)
          if (element && onItemSelected) {
            onItemSelected(index, element)
          }
        }
        document.removeEventListener('pointermove', onPointerMove)
        document.removeEventListener('pointerleave', onPointerExit)
        document.removeEventListener('pointerup', onPointerExit)
      }

      document.addEventListener('pointermove', onPointerMove)
      document.addEventListener('pointerleave', onPointerExit)
      document.addEventListener('pointerup', onPointerExit)
    },
    [gap, items, externalOnPointerDown, onItemFocused, onItemSelected],
  )

  useLayoutEffect(() => {
    const calculateSize = () => {
      if (!internalListRef.current) {
        return
      }
      const galleryWidth = internalListRef.current?.clientWidth
      const firstItemWidth = internalItemRefs.current.at(0)?.clientWidth
      const lastItemWidth = internalItemRefs.current.at(-1)?.clientWidth

      if (galleryWidth === undefined || firstItemWidth === undefined || lastItemWidth === undefined) {
        return
      }

      const firstPad = (galleryWidth - firstItemWidth) / 2
      const lastPad = (galleryWidth - lastItemWidth) / 2
      internalListRef.current.style = `--first-pad: ${firstPad}px; --last-pad: ${lastPad}px;`
    }

    calculateSize()
    if (internalListRef.current) {
      const list = internalListRef.current
      list.addEventListener('resize', calculateSize)
      return () => list.removeEventListener('resize', calculateSize)
    }
  })

  useEffect(() => {
    if (externalCurrentIndex === internalScrollIndex.current) {
      return
    }
    const item = internalItemRefs.current.at(externalCurrentIndex)
    if (item) {
      item.scrollIntoView(scrollIntoViewOptions)
      internalScrollIndex.current = externalCurrentIndex
    }
  }, [externalCurrentIndex])

  return (
    <div className={cx('media', mediaListClass, className)} ref={listRef} onPointerDown={internalOnPointerDown} {...props}>
      {items.map((x, i) =>
        x?.type === 'image' ? (
          <img
            ref={(ref) => {
              setItemRef(i, ref)
            }}
            className={cx('entry', externalCurrentIndex === i ? 'focused' : 'unfocused')}
            src={x.url}
            alt={x.alt}
            key={x.id}
            draggable={false}
          />
        ) : (
          <Video
            key={x.id}
            className={cx('entry', externalCurrentIndex === i ? 'focused' : 'unfocused')}
            playState={playState}
            setPlayState={setPlayState}
            ref={(ref) => setItemRef(i, ref)}
            src={x.url}
            loop={x.loop}
            controls={x.controls}
            alt={x.alt}
            autoPlay={x.autoplay}
            preload="auto"
          />
        ),
      )}
    </div>
  )
}
